// Import Vue and the main application component.
import PickstarCommon from 'pickstar-common'
import Vue from 'vue'
import App from '@/App'

// Import the router and Vuex store.
import router from '@/router'
import store from 'vx/store'

// Import utilities and helpers that need to be setup prior to the app.
import 'utils/http'
import 'utils/components'
import 'directives'
import 'filters'
import 'mixins'
import authorized from './mixins/authorized'

// Import most of our dependencies here. Some dependencies are imported
// on a per-component basis depending on how the dependency is used.
import 'bootstrap'
import 'bootstrap-slider'
import 'bootstrap-drawer/js/drawer'
import 'moment'
import Vuelidate from 'vuelidate'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import * as VueGoogleMaps from 'vue2-google-maps'

import * as PickstarUserAnalytics from '@pickstar/user-analytics'
import * as PickstarUserAnalyticsWebClient from '@pickstar/user-analytics-web-client'

import i18n from './plugins/i18n'

import initIubenda from './plugins/iubenda'
import * as Sentry from '@sentry/browser'
import VueAnalytics from 'vue-analytics'
// import VueIntercom from '@mathieustan/vue-intercom'
import VueGtm from 'vue-gtm'
import VueTelInput from 'vue-tel-input'
import 'vue-tel-input/dist/vue-tel-input.css'

const isVlast = window.location.hostname.indexOf('govlast.com') > -1
const isProdEnv = (process.env.NODE_ENV === 'production')
const appInstance = isProdEnv
  ? (isVlast ? PickstarUserAnalytics.V2.Environment.PRODUCTION_VLAST : PickstarUserAnalytics.V2.Environment.PRODUCTION_MARKETPLACE)
  : (isVlast ? PickstarUserAnalytics.V2.Environment.STAGING_VLAST : PickstarUserAnalytics.V2.Environment.STAGING_MARKETPLACE)
const analyticsImplementation = new PickstarUserAnalyticsWebClient.mixpanel.MixpanelWebClient(appInstance, process.env.NODE_ENV)

// Import and configure Google Maps.
initIubenda({
  countryDetection: true,
  cookiePolicyInOtherWindow: true,
  consentOnContinuedBrowsing: false,
  perPurposeConsent: true,
  enableCMP: true,
  googleAdditionalConsentMode: true,
  isTCFConsentGlobal: false,
  lang: 'en',
  siteId: process.env.IUBENDA_SITE_ID,
  floatingPreferencesButtonDisplay: 'bottom-right',
  cookiePolicyId: 63603662,
  gdprAppliesGlobally: false,
  banner: {
    acceptButtonDisplay: true,
    customizeButtonDisplay: true,
    acceptButtonColor: '#FC9066',
    acceptButtonCaptionColor: 'white',
    customizeButtonColor: '#FFFFFF',
    customizeButtonCaptionColor: '#B5B5B5',
    rejectButtonDisplay: true,
    rejectButtonColor: '#FFFFFF',
    rejectButtonCaptionColor: '#BDBDBD',
    position: 'float-bottom-center',
    textColor: 'black',
    backgroundColor: 'white'
  },
  callback: {
    onPreferenceExpressedOrNotNeeded (preference) {
      window.dataLayer.push({
        event: 'iubenda_consent',
        consentEnhanced:
          !preference || preference.purposes[3]
            ? 'true'
            : 'false',
        consentAnalytics:
          !preference || preference.purposes[4]
            ? 'true'
            : 'false',
        consentTargeting:
          !preference || preference.purposes[5]
            ? 'true'
            : 'false'
      })
      if (!preference || preference.purposes[4]) {
        Vue.prototype.$analytics = new PickstarUserAnalytics.AnalyticsService(
          analyticsImplementation
        )
      } else {
        if (
          Vue.prototype.$analytics &&
          Vue.prototype.$analytics !==
          { track: () => {}, identify: () => {} }
        ) {
          Vue.prototype.$analytics.setOptOut(true)
        }
        Vue.prototype.$analytics = { track: () => {}, identify: () => {} }
      }
      if (!preference) {
        document.body.classList.add('consent-unnecessary')
      }
    }
  }
})

Vue.use(Vuelidate)
Vue.use(VueTelInput)

// Create the Vue application and mount it to the "app" div.
Vue.use(PickstarCommon, { isProduction: process.env.NODE_ENV === 'production' })

Vue.use(VueGoogleMaps, {
  load: {
    key: process.env.MAPS_API_KEY,
    libraries: 'places',
    loading: 'async'
  }
})

try {
  Vue.use(VueAnalytics, {
    id: process.env.GA_TRACKING_CODE,
    router,
    debug: {
      enabled: false,
      trace: false,
      sendHitTask: true
    }
  })
} catch (err) {
  // This is just to capture a failed load due to adblock
}
Vue.use(VueGtm, {
  id: process.env.GTM_ID,
  queryParams: {
    gtm_auth: process.env.GTM_AUTH,
    gtm_preview: process.env.GTM_PREVIEW,
    gtm_cookies_win: process.env.GTM_COOKIES_WIN
  },
  vueRouter: router
})

// Intercom for MP only
// if (!isVlast) {
//   Vue.use(VueIntercom, { appId: process.env.INTERCOM_KEY })
// }

if (process.env.SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.SENTRY_DSN,
    integrations: [new Sentry.Integrations.Vue({
      Vue,
      attachProps: true
    })]
  })
}

Vue.mixin(authorized)

window.Laravel = { publicURL: process.env.PUBLIC_URL.replace('{tenant}', window.location.hostname.split('.')[0]) }
const VueApp = new Vue({
  router,
  store,
  i18n,
  template: '<App/>',
  components: {
    App
  }
}).$mount('#app')

export default VueApp
